// angular
import { Component, effect, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
// components
import { InputComponent } from '@app/core/components/input/input.component';
import { ButtonComponent } from '@app/core/components/button/button.component';
import { SignupDialogComponent } from '../signup-dialog/signup-dialog.component';
import { PhoneNumberComponent } from '@app/core/components/phone-number/phone-number.component';
// services
import { AuthService } from '@app/shared/services/auth.service';
import { StorageService } from '@app/shared/services/storage.service';
import { InitService } from '@app/shared/services/init.service';
import { RecaptchaService } from '@app/shared/services/recaptcha.service';
import { DialogService } from '@app/core/shared/services/dialog.service';
// utils
import { DialogRef } from '@app/core/shared/utils/dialog/dialog-ref';
// types & env
import { RecaptchaData } from '@app/shared/types/recaptcha-data.type';
import { environment } from '@environments/environment';
import {ForgotPasswordDialogComponent} from "@core/dialogs/forgot-password-dialog/forgot-password-dialog.component";
import {LoadingService} from "@shared/services/loading.service";
import {MatomoTrackClickDirective} from "ngx-matomo-client";

@Component({
  selector: 'pwj-login-dialog',
  standalone: true,
  imports: [
    InputComponent,
    ButtonComponent,
    TranslateModule,
    ReactiveFormsModule,
    PhoneNumberComponent,
    MatomoTrackClickDirective,
  ],
  templateUrl: './login-dialog.component.html',
  styleUrl: './login-dialog.component.scss',
})
export class LoginDialogComponent {
  public loginForm!: FormGroup;
  public formIsSubmitting: boolean = false;
  public isDisabledSubmitBtn: boolean = false;

  // dependencies
  private readonly _fb = inject(FormBuilder);
  private readonly _dialogRef = inject(DialogRef);
  private readonly _dialogService = inject(DialogService);
  private readonly _authService = inject(AuthService);
  private readonly _storageService = inject(StorageService);
  private readonly _initService = inject(InitService);
  private readonly _recaptchaService = inject(RecaptchaService);
  private readonly _loadingService = inject(LoadingService);

  constructor() {
    this._recaptchaService.recaptchaValidation('login_form');
    // initialize login form
    this.loginForm = this._fb.group({
      mobile: ['', Validators.required],
      password: ['', Validators.required],
    });

    effect(() => {
      if (this._recaptchaService.recaptchaData() !== null) {
        const captchaData =
          this._recaptchaService.recaptchaData() as RecaptchaData;

        if (captchaData.score < environment.recaptcha.targetScore) {
          this.isDisabledSubmitBtn = true;
        }
      }
    });

    this.loginForm.valueChanges.subscribe(_ => {
      if (!this.loginForm.dirty) {
        this.isDisabledSubmitBtn = true;
      }
    });
  }

  public close() {
    this._dialogRef.close();
  }

  public openSignupDialog() {
    // navigate to panel signup page
    this.close();
    this._dialogService.open(SignupDialogComponent, {
      width: 'none',
      height: 'none',
    });
  }

  public openForgotDialog(){
    this.close();
    this._dialogService.open(ForgotPasswordDialogComponent, {
      width: 'none',
      height: 'none',
    });
  }

  public login() {
    // check if form is valid then login to account
    this.loginForm.markAllAsTouched();
    if (this.loginForm.invalid || !this.loginForm.dirty) return;
    this._loadingService.setLoading(true);
    this._authService.login(this.loginForm.value).subscribe({
      next: (res) => {
        // check if response is valid then save tokens & initialize app
        if (res) {
          this._storageService.setToken(res.access_token);
          this._storageService.setRefreshToken(res.refresh_token);
          this._initService.init();

          if (res.mobile_verified) {
            this.close();
            window.location.reload();
          } else {
            const userData = {
              mobile: this.loginForm.value?.mobile,
              hash: res.hash,
            };

            this._authService.updateUserSignUpState({
              mobile_verified: res.mobile_verified,
              mobile: userData.mobile,
              state: 1,
            });
            this.close();
            this._dialogService.open(SignupDialogComponent, {
              width: 'none',
              height: 'none',
              data: userData,
            });
          }
          this._loadingService.setLoading(false);
        }
      },
      error: ()=> this._loadingService.setLoading(false),
      complete: ()=> this._loadingService.setLoading(false),
    });
  }
}
